import type { Config } from 'tailwindcss';
import plugin from 'tailwindcss/plugin';
import animate from 'tailwindcss-animate';

/**
 * Пресет плотности «density-compact».
 *
 * У AlignUI строка таблицы 64 точки — это карточная таблица под двадцать строк. У нас в
 * ведомости 281 строка: при 64 точках получается полотно высотой 18 000 точек, по которому
 * финансовый директор будет искать человека прокруткой. При 28 точках то же полотно — около
 * 7 900, а на экране помещается примерно 25 строк вместо 11.
 *
 * Поэтому плотность живёт отдельным пресетом, а не правками в компонентах:
 *   * токены AlignUI (цвета, тени, типографика) подключаются рядом и остаются нетронутыми —
 *     их обновление не затирает плотность;
 *   * высота строки задана переменной, а не числом в каждом компоненте: три режима плотности
 *     переключаются классом на корне сетки, без пересборки;
 *   * скруглений и зазоров между ячейками нет вовсе. Каждый лишний пиксель здесь умножается
 *     на 281, а скругление в плотной сетке ещё и режет цифру на краю ячейки.
 *
 * Три высоты, а не одна: 28 — рабочая для ведомости, 30 — для списков средней плотности,
 * 32 — предел, ниже которого попадание мышью в строку становится делом удачи.
 */
/**
 * Варианты среды: где мы и чем показывает человек.
 *
 * Признаки живут атрибутами на `<html>` (`shared/среда`), а сюда приходят вариантами — тогда
 * разметка читается вслух («узко:hidden» — в узком спрятать) и не заводит ни одной ветки в
 * коде. Ветка в коде перерисовывает дерево при повороте планшета; вариант просто перекладывает
 * стили.
 */
export const вариантыСреды = plugin(({ addVariant }) => {
  // Ширина. Три состояния, а не «мобильный и десктоп»: у планшета ландшафт и портрет — это
  // два разных продукта по раскладке, и сваливать их в один режим значит потерять один из них.
  // Слишком узко, чтобы работать: кабинет говорит об этом прямо, а не рисует кашу.
  addVariant('мало', 'html[data-ширина="мало"] &');
  addVariant('узко', 'html[data-ширина="узко"] &');
  addVariant('средне', 'html[data-ширина="средне"] &');
  addVariant('широко', 'html[data-ширина="широко"] &');
  // «Не широко» — общее для планшета в любой ориентации: узкая полоса разделов нужна обоим.
  addVariant('тесно', ['html[data-ширина="узко"] &', 'html[data-ширина="средне"] &']);
  // Палец. Крупные цели касания включаются здесь и только здесь: поднимать высоту кнопок на
  // рабочем столе нельзя — там плотность замерена и обоснована.
  addVariant('тач', 'html[data-ввод="тач"] &');
  // Настоящая клавиатура. Подпись сочетания клавиш, которое нечем нажать, — это ложь на экране.
  addVariant('клавиша', 'html[data-клавиатура="есть"] &');
  // Выбрана строка списка. В узком режиме это решает, что показывать: список или карточку.
  addVariant('строка', 'html[data-строка="есть"] &');
  addVariant('безстроки', 'html:not([data-строка="есть"]) &');

  // ⚠️ Составные варианты объявлены ОТДЕЛЬНО, и это не удобство, а необходимость.
  //
  // Цепочка `узко:строка:hidden` собирается механически: каждый вариант подставляет свой
  // селектор в предыдущий, и получается `html[data-ширина="узко"] html[data-строка="есть"] &`
  // — то есть «html внутри html». Такого в документе не бывает никогда, правило не срабатывает
  // ни разу, а ошибки нет: стили собираются, сборка зелёная, в браузере просто ничего не
  // происходит. Поймано живым прогоном 10.09.2026 — в портрете планшета карточка человека не
  // открывалась вовсе, список оставался на месте, а деталь уезжала за правый край экрана.
  addVariant('узко-строка', 'html[data-ширина="узко"][data-строка="есть"] &');
  addVariant('узко-безстроки', 'html[data-ширина="узко"]:not([data-строка="есть"]) &');
});

export const densityCompact = {
  theme: {
    extend: {
      spacing: {
        // Отступы внутри ячейки. По вертикали почти ноль: высоту держит сама строка.
        'cell-x': '8px',
        'cell-y': '2px',
      },
      height: {
        // Высота приложения за вычетом экранной клавиатуры (`shared/среда`). На компьютере
        // переменная равна нулю, и это ровно прежние 100dvh; на планшете она уменьшает окно
        // ровно на закрытую клавиатурой часть, и нижние поля перестают быть недосягаемыми.
        приложение: 'calc(100dvh - var(--bark-клавиатура, 0px))',
        row: 'var(--bark-row-h, 28px)',
        'row-28': '28px',
        'row-30': '30px',
        'row-32': '32px',
        // Шапка чуть выше строки: в ней два уровня подписи (колонка и её единица измерения).
        'row-head': '34px',
      },
      minWidth: {
        // Денежная колонка не сжимается ниже ширины «1 234 567,89» — иначе сумма покажется
        // многоточием, а это ровно то число, ради которого человек и открыл экран.
        money: '104px',
        // Название месяца в календаре. Ширина закреплена по самому длинному — «Сентябрь 2026»:
        // иначе стрелки «назад» и «вперёд» прыгают под курсором при каждом переключении.
        period: '132px',
      },
      width: {
        // Колонка списка раздела. Два значения, а не свободное число: свободное разъедется по
        // разделам за неделю. `list` — там, где в строке числа и метки; `list-names` — там, где
        // фамилия, имя, отчество, объект и город, то есть в «Людях» и «Объектах». При общей
        // ширине в 384 точки в «Людях» обрезалась именно фамилия, потому что метки стоят
        // справа и не сжимаются.
        list: '368px',
        'list-names': '416px',
        // Полоса разделов. На рабочем столе значок и название в строку — 240 точек. На
        // планшете те же 240 съедают четверть ландшафта (1133 точки), и правая колонка
        // начинает резать заголовки величин: «Неподтверждён…», «Долги сотрудни…». Узкая
        // полоса ставит название ПОД значок и возвращает содержимому 168 точек.
        rail: '240px',
        'rail-tight': '72px',
        // Выдвижная панель. На рабочем столе 384 точки — узкая колонка поверх детали, и
        // деталь под ней остаётся читаемой. В ландшафте планшета детали всего 645 точек, и
        // те же 384 оставляют от неё 261 — панель перестаёт быть «поверх» и становится
        // «вместо», не выглядя при этом как «вместо». Поэтому там она шире и честно
        // закрывает собой работу, а в портрете разворачивается на весь экран.
        panel: '384px',
        'panel-wide': '520px',
      },
      maxWidth: {
        // Читаемая мера для текста и рядов величин. Без неё пояснение растягивалось на всю
        // ширину окна (1600 точек), и глаз терял начало следующей строки, а соседние величины
        // расходились на 600 точек и переставали читаться как разложение одного числа.
        prose: '42rem',
        metrics: '48rem',
      },
      fontSize: {
        // Мелкая типографика сетки. Ниже 11 точек цифры перестают различаться на плохом
        // мониторе, а у клиента в кабинете именно такой.
        grid: ['12px', { lineHeight: '16px' }],
        'grid-sm': ['11px', { lineHeight: '14px' }],
        'grid-head': ['11px', { lineHeight: '14px', letterSpacing: '0.02em' }],
      },
      borderRadius: {
        // Явный ноль, а не отсутствие значения: `rounded-grid` читается в разметке как
        // осознанное решение, а не как забытый класс.
        grid: '0px',
      },
    },
  },
  plugins: [
    plugin(({ addComponents, addUtilities }) => {
      addComponents({
        // ── Ряд подписанных величин ────────────────────────────────────────────────────────
        // Доли раскладываются по ширине КОНТЕЙНЕРА, а не окна: полоса итогов ведомости стоит
        // в правой колонке рядом со списком, и на ноутбучном экране ей достаётся 660 точек при
        // окне в 1280. Медиазапросы видели «широкое окно», ставили пять долей по 130 точек, и
        // подписи резались посреди слова («– Официа…», «= Всего к вы…»).
        //
        // Минимальная доля 11rem — ширина, на которой помещается самая длинная подпись ряда
        // («– Официальная часть и удержания» переносится в две строки, но не режется).
        // Разделители рисуются рамкой слева у всех долей, кроме первой в строке: при переносе
        // на вторую строку вертикальная черта в начале строки выглядела бы обрывком.
        '.bark-metrics-2, .bark-metrics-3, .bark-metrics-4, .bark-metrics-5, .bark-metrics-6': {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))',
          columnGap: '1.25rem',
          rowGap: '1rem',
          // Обрезка нужна разделителям: у первой доли КАЖДОЙ строки черта уезжает за левый край
          // и здесь пропадает. Без этого при переносе ряда на вторую строку черта повисала бы
          // в начале строки обрывком, а доля выглядела бы сдвинутой относительно верхней.
          overflow: 'hidden',
        },
        '.bark-metrics-2 > *, .bark-metrics-3 > *, .bark-metrics-4 > *, .bark-metrics-5 > *, .bark-metrics-6 > *':
          {
            // Черта встаёт по СЕРЕДИНЕ зазора, а текст остаётся выровненным по краю своей доли:
            // отрицательное поле и равный ему отступ гасят друг друга.
            borderLeft: '1px solid hsl(var(--stroke-soft-200))',
            marginLeft: '-0.625rem',
            paddingLeft: '0.625rem',
          },

        // Класс на корне сетки. Переключение плотности — смена одного класса, а не правка
        // тридцати компонентов.
        '.density-compact': { '--bark-row-h': '28px' },
        '.density-regular': { '--bark-row-h': '30px' },
        '.density-comfortable': { '--bark-row-h': '32px' },

        '.bark-grid-row': {
          height: 'var(--bark-row-h, 28px)',
          // Строка не растягивается по содержимому: одна выросшая строка ломает расчёт
          // виртуализации, и прокрутка начинает дёргаться.
          minHeight: 'var(--bark-row-h, 28px)',
          maxHeight: 'var(--bark-row-h, 28px)',
        },
        '.bark-grid-cell': {
          paddingLeft: '8px',
          paddingRight: '8px',
          paddingTop: '0',
          paddingBottom: '0',
          borderRadius: '0',
          // Текст в одну строку: перенос внутри ячейки сдвинул бы всю сетку.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },

        // Содержимое ячейки не имеет права распирать строку.
        //
        // Ограничение висит на ЭЛЕМЕНТЕ внутри ячейки, а не на строке и не на самой ячейке, и
        // это не стилистический выбор: для `table-row` и `table-cell` браузер считает высоту
        // минимумом и `max-height` попросту игнорирует. Правило выше («строка не растягивается»)
        // без этой строки было объявлением, а не правилом — замер 18.08.2026 в безголовом
        // браузере: двухстрочная фамилия в официальном контуре давала строку 33 точки вместо 28.
        // Виртуализация раскладывает строки по 28, и на такой разнице внизу ведомости каретка
        // встаёт не на ту строку, которую видит человек.
        //
        // Минус точка — это нижняя рамка ячейки: при раздельных рамках она входит в высоту
        // строки, и содержимое ровно в высоту строки давало бы 29 точек вместо 28 (замер).
        '.bark-grid-cell > *': {
          maxHeight: 'calc(var(--bark-row-h, 28px) - 1px)',
          overflow: 'hidden',
        },
      });

      addUtilities({
        // Моноширинные цифры. Без них разряды в соседних строках не встают друг под друга, и
        // столбец сумм перестаёт читаться взглядом сверху вниз — а именно так его и читают.
      });
    }),
  ],
} satisfies Partial<Config>;

// Три набора токенов экспортируются по образцу исходного конфига AlignUI: их читает
// src/utils/cn.ts, чтобы tailwind-merge знал, какие наши классы соперничают между собой.
// Убрать экспорт — и cnExt начнёт молча оставлять оба конфликтующих класса.

export const texts = {
  'title-h1': ['3.5rem', { lineHeight: '4rem', letterSpacing: '-0.01em', fontWeight: '500' }],
  'title-h2': ['3rem', { lineHeight: '3.5rem', letterSpacing: '-0.01em', fontWeight: '500' }],
  'title-h3': ['2.5rem', { lineHeight: '3rem', letterSpacing: '-0.01em', fontWeight: '500' }],
  'title-h4': ['2rem', { lineHeight: '2.5rem', letterSpacing: '-0.005em', fontWeight: '500' }],
  'title-h5': ['1.5rem', { lineHeight: '2rem', letterSpacing: '0em', fontWeight: '500' }],
  'title-h6': ['1.25rem', { lineHeight: '1.75rem', letterSpacing: '0em', fontWeight: '500' }],
  'label-xl': ['1.5rem', { lineHeight: '2rem', letterSpacing: '-0.015em', fontWeight: '500' }],
  'label-lg': ['1.125rem', { lineHeight: '1.5rem', letterSpacing: '-0.015em', fontWeight: '500' }],
  'label-md': ['1rem', { lineHeight: '1.5rem', letterSpacing: '-0.011em', fontWeight: '500' }],
  'label-sm': ['.875rem', { lineHeight: '1.25rem', letterSpacing: '-0.006em', fontWeight: '500' }],
  'label-xs': ['.75rem', { lineHeight: '1rem', letterSpacing: '0em', fontWeight: '500' }],
  'paragraph-xl': ['1.5rem', { lineHeight: '2rem', letterSpacing: '-0.015em', fontWeight: '400' }],
  'paragraph-lg': [
    '1.125rem',
    { lineHeight: '1.5rem', letterSpacing: '-0.015em', fontWeight: '400' },
  ],
  'paragraph-md': ['1rem', { lineHeight: '1.5rem', letterSpacing: '-0.011em', fontWeight: '400' }],
  'paragraph-sm': [
    '.875rem',
    { lineHeight: '1.25rem', letterSpacing: '-0.006em', fontWeight: '400' },
  ],
  'paragraph-xs': ['.75rem', { lineHeight: '1rem', letterSpacing: '0em', fontWeight: '400' }],
  'subheading-md': ['1rem', { lineHeight: '1.5rem', letterSpacing: '0.06em', fontWeight: '500' }],
  'subheading-sm': [
    '.875rem',
    { lineHeight: '1.25rem', letterSpacing: '0.06em', fontWeight: '500' },
  ],
  'subheading-xs': ['.75rem', { lineHeight: '1rem', letterSpacing: '0.04em', fontWeight: '500' }],
  'subheading-2xs': [
    '.6875rem',
    { lineHeight: '.75rem', letterSpacing: '0.02em', fontWeight: '500' },
  ],
  'doc-label': ['1.125rem', { lineHeight: '2rem', letterSpacing: '-0.015em', fontWeight: '500' }],
  'doc-paragraph': [
    '1.125rem',
    { lineHeight: '2rem', letterSpacing: '-0.015em', fontWeight: '400' },
  ],
} as unknown as Record<string, string>;

export const shadows = {
  'regular-xs': '0 1px 2px 0 #0a0d1408',
  'regular-sm': '0 2px 4px #1b1c1d0a',
  'regular-md': '0 16px 32px -12px #0e121b1a',
  'button-primary-focus': [
    '0 0 0 2px theme(colors.bg[white-0])',
    '0 0 0 4px theme(colors.primary[alpha-10])',
  ],
  'button-important-focus': [
    '0 0 0 2px theme(colors.bg[white-0])',
    '0 0 0 4px theme(colors.neutral[alpha-16])',
  ],
  'button-error-focus': [
    '0 0 0 2px theme(colors.bg[white-0])',
    '0 0 0 4px theme(colors.red[alpha-10])',
  ],
  'fancy-buttons-neutral': ['0 1px 2px 0 #1b1c1d7a', '0 0 0 1px #242628'],
  'fancy-buttons-primary': ['0 1px 2px 0 #0e121b3d', '0 0 0 1px theme(colors.primary[base])'],
  'fancy-buttons-error': ['0 1px 2px 0 #0e121b3d', '0 0 0 1px theme(colors.error[base])'],
  'fancy-buttons-stroke': ['0 1px 3px 0 #0e121b1f', '0 0 0 1px theme(colors.stroke[soft-200])'],
  'toggle-switch': ['0 6px 10px 0 #0e121b0f', '0 2px 4px 0 #0e121b08'],
  'switch-thumb': ['0 4px 8px 0 #1b1c1d0f', '0 2px 4px 0 #0e121b14'],
  tooltip: ['0 12px 24px 0 #0e121b0f', '0 1px 2px 0 #0e121b08'],
  'custom-xs': [
    '0 0 0 1px rgba(51, 51, 51, 0.04)',
    '0 4px 8px -2px rgba(51, 51, 51, 0.06)',
    '0 2px 4px rgba(51, 51, 51, 0.04)',
    '0 1px 2px rgba(51, 51, 51, 0.04)',
    'inset 0 -1px 1px -0.5px rgba(51, 51, 51, 0.06)',
  ],
  'custom-sm': [
    '0 0 0 1px rgba(51, 51, 51, 0.04)',
    '0 16px 8px -8px rgba(51, 51, 51, 0.01)',
    '0 12px 6px -6px rgba(51, 51, 51, 0.02)',
    '0 5px 5px -2.5px rgba(51, 51, 51, 0.08)',
    '0 1px 3px -1.5px rgba(51, 51, 51, 0.16)',
    'inset 0 -0.5px 0.5px rgba(51, 51, 51, 0.08)',
  ],
  'custom-md': [
    '0 0 0 1px rgba(51, 51, 51, 0.04)',
    '0 1px 1px 0.5px rgba(51, 51, 51, 0.04)',
    '0 3px 3px -1.5px rgba(51, 51, 51, 0.02)',
    '0 6px 6px -3px rgba(51, 51, 51, 0.04)',
    '0 12px 12px -6px rgba(51, 51, 51, 0.04)',
    '0 24px 24px -12px rgba(51, 51, 51, 0.04)',
    '0 48px 48px -24px rgba(51, 51, 51, 0.04)',
    'inset 0 -1px 1px -0.5px rgba(51, 51, 51, 0.06)',
  ],
  'custom-lg': [
    '0 0 0 1px rgba(51, 51, 51, 0.04)',
    '0 1px 1px 0.5px rgba(51, 51, 51, 0.04)',
    '0 3px 3px -1.5px rgba(51, 51, 51, 0.02)',
    '0 6px 6px -3px rgba(51, 51, 51, 0.04)',
    '0 12px 12px -6px rgba(51, 51, 51, 0.04)',
    '0 24px 24px -12px rgba(51, 51, 51, 0.04)',
    '0 48px 48px -24px rgba(51, 51, 51, 0.04)',
    '0 96px 96px -32px rgba(51, 51, 51, 0.06)',
    'inset 0 -1px 1px -0.5px rgba(51, 51, 51, 0.06)',
  ],
} as unknown as Record<string, string>;

// Скругления: РОЛЬ, а не вкус. Канон записан здесь 25.08.2026, после того как выяснилось,
// что двадцать карточек и полей в продукте рисовались острыми углами: код писал `rounded-12`,
// `rounded-8` и `rounded-4`, а таких классов в теме нет вовсе — браузер их молча выбрасывает.
// Значения при этом были: та же двенадцатка живёт под именем `rounded-xl`. То есть беда была
// не в отсутствии токена, а в ДВУХ СЛОВАРЯХ на одно и то же, из которых половина второго —
// опечатки. Отсюда правило: числовых имён в теме ровно два, остальные ступени зовутся так, как
// их зовёт Tailwind, и каждая закреплена за одной ролью.
//
//   роль                                        значение   класс
//   метка, чип, полоса доли                     4          rounded
//   переключатель, флажок                       6          rounded-md
//   ВЛОЖЕННАЯ ПЛАШКА внутри карточки            8          rounded-lg
//   ОРГАН УПРАВЛЕНИЯ: кнопка, поле, список      10         rounded-10   ← ступень AlignUI
//   КАРТОЧКА, блок, секция                      12         rounded-xl
//   всплывающий список                          16         rounded-2xl
//   окно и карточка входа                       20         rounded-20   ← ступень AlignUI
//   круг и пилюля                               —          rounded-full
//
// Признак роли машинный, спорить не о чем: кольцо или рамка на белом фоне — карточка;
// приглушённая заливка без кольца — вложенная плашка; то, во что тыкают пальцем, — орган.
// Держит это `web/src/test/оформление.test.ts`: он краснеет на числовом скруглении,
// которого нет в теме.
export const borderRadii = {
  '10': '.625rem',
  '20': '1.25rem',
} as unknown as Record<string, string>;

export default {
  content: ['./index.html', './src/**/*.{ts,tsx}'],

  // Плотность идёт пресетом, а не правкой theme: обновление токенов AlignUI перезаписывает
  // theme целиком, и вписанная туда высота строки исчезла бы вместе с ним.
  presets: [densityCompact],

  darkMode: 'class',

  theme: {
    extend: {
      // Семантические токены AlignUI. Перенесены из template-hr/tailwind.config.ts набора
      // дизайн-системы (шаг установки по манифесту MCP-сервера alignui). Значения цветов
      // живут CSS-переменными в src/app/globals.css: класс остаётся один, тема меняется
      // подменой переменных (светлая/тёмная). Пресет плотности выше не трогается.
      colors: {
        gray: {
          '0': 'hsl(var(--gray-0))',
          '50': 'hsl(var(--gray-50))',
          '100': 'hsl(var(--gray-100))',
          '200': 'hsl(var(--gray-200))',
          '300': 'hsl(var(--gray-300))',
          '400': 'hsl(var(--gray-400))',
          '500': 'hsl(var(--gray-500))',
          '600': 'hsl(var(--gray-600))',
          '700': 'hsl(var(--gray-700))',
          '800': 'hsl(var(--gray-800))',
          '900': 'hsl(var(--gray-900))',
          '950': 'hsl(var(--gray-950))',
          'alpha-24': 'hsl(var(--gray-alpha-24))',
          'alpha-16': 'hsl(var(--gray-alpha-16))',
          'alpha-10': 'hsl(var(--gray-alpha-10))',
        },
        slate: {
          '0': 'hsl(var(--slate-0))',
          '50': 'hsl(var(--slate-50))',
          '100': 'hsl(var(--slate-100))',
          '200': 'hsl(var(--slate-200))',
          '300': 'hsl(var(--slate-300))',
          '400': 'hsl(var(--slate-400))',
          '500': 'hsl(var(--slate-500))',
          '600': 'hsl(var(--slate-600))',
          '700': 'hsl(var(--slate-700))',
          '800': 'hsl(var(--slate-800))',
          '900': 'hsl(var(--slate-900))',
          '950': 'hsl(var(--slate-950))',
          'alpha-24': 'hsl(var(--slate-alpha-24))',
          'alpha-16': 'hsl(var(--slate-alpha-16))',
          'alpha-10': 'hsl(var(--slate-alpha-10))',
        },
        neutral: {
          '0': 'hsl(var(--neutral-0))',
          '50': 'hsl(var(--neutral-50))',
          '100': 'hsl(var(--neutral-100))',
          '200': 'hsl(var(--neutral-200))',
          '300': 'hsl(var(--neutral-300))',
          '400': 'hsl(var(--neutral-400))',
          '500': 'hsl(var(--neutral-500))',
          '600': 'hsl(var(--neutral-600))',
          '700': 'hsl(var(--neutral-700))',
          '800': 'hsl(var(--neutral-800))',
          '900': 'hsl(var(--neutral-900))',
          '950': 'hsl(var(--neutral-950))',
          'alpha-24': 'hsl(var(--neutral-alpha-24))',
          'alpha-16': 'hsl(var(--neutral-alpha-16))',
          'alpha-10': 'hsl(var(--neutral-alpha-10))',
        },
        blue: {
          '50': 'hsl(var(--blue-50))',
          '100': 'hsl(var(--blue-100))',
          '200': 'hsl(var(--blue-200))',
          '300': 'hsl(var(--blue-300))',
          '400': 'hsl(var(--blue-400))',
          '500': 'hsl(var(--blue-500))',
          '600': 'hsl(var(--blue-600))',
          '700': 'hsl(var(--blue-700))',
          '800': 'hsl(var(--blue-800))',
          '900': 'hsl(var(--blue-900))',
          '950': 'hsl(var(--blue-950))',
          'alpha-24': 'hsl(var(--blue-alpha-24))',
          'alpha-16': 'hsl(var(--blue-alpha-16))',
          'alpha-10': 'hsl(var(--blue-alpha-10))',
        },
        orange: {
          '50': 'hsl(var(--orange-50))',
          '100': 'hsl(var(--orange-100))',
          '200': 'hsl(var(--orange-200))',
          '300': 'hsl(var(--orange-300))',
          '400': 'hsl(var(--orange-400))',
          '500': 'hsl(var(--orange-500))',
          '600': 'hsl(var(--orange-600))',
          '700': 'hsl(var(--orange-700))',
          '800': 'hsl(var(--orange-800))',
          '900': 'hsl(var(--orange-900))',
          '950': 'hsl(var(--orange-950))',
          'alpha-24': 'hsl(var(--orange-alpha-24))',
          'alpha-16': 'hsl(var(--orange-alpha-16))',
          'alpha-10': 'hsl(var(--orange-alpha-10))',
        },
        red: {
          '50': 'hsl(var(--red-50))',
          '100': 'hsl(var(--red-100))',
          '200': 'hsl(var(--red-200))',
          '300': 'hsl(var(--red-300))',
          '400': 'hsl(var(--red-400))',
          '500': 'hsl(var(--red-500))',
          '600': 'hsl(var(--red-600))',
          '700': 'hsl(var(--red-700))',
          '800': 'hsl(var(--red-800))',
          '900': 'hsl(var(--red-900))',
          '950': 'hsl(var(--red-950))',
          'alpha-24': 'hsl(var(--red-alpha-24))',
          'alpha-16': 'hsl(var(--red-alpha-16))',
          'alpha-10': 'hsl(var(--red-alpha-10))',
        },
        green: {
          '50': 'hsl(var(--green-50))',
          '100': 'hsl(var(--green-100))',
          '200': 'hsl(var(--green-200))',
          '300': 'hsl(var(--green-300))',
          '400': 'hsl(var(--green-400))',
          '500': 'hsl(var(--green-500))',
          '600': 'hsl(var(--green-600))',
          '700': 'hsl(var(--green-700))',
          '800': 'hsl(var(--green-800))',
          '900': 'hsl(var(--green-900))',
          '950': 'hsl(var(--green-950))',
          'alpha-24': 'hsl(var(--green-alpha-24))',
          'alpha-16': 'hsl(var(--green-alpha-16))',
          'alpha-10': 'hsl(var(--green-alpha-10))',
        },
        yellow: {
          '50': 'hsl(var(--yellow-50))',
          '100': 'hsl(var(--yellow-100))',
          '200': 'hsl(var(--yellow-200))',
          '300': 'hsl(var(--yellow-300))',
          '400': 'hsl(var(--yellow-400))',
          '500': 'hsl(var(--yellow-500))',
          '600': 'hsl(var(--yellow-600))',
          '700': 'hsl(var(--yellow-700))',
          '800': 'hsl(var(--yellow-800))',
          '900': 'hsl(var(--yellow-900))',
          '950': 'hsl(var(--yellow-950))',
          'alpha-24': 'hsl(var(--yellow-alpha-24))',
          'alpha-16': 'hsl(var(--yellow-alpha-16))',
          'alpha-10': 'hsl(var(--yellow-alpha-10))',
        },
        purple: {
          '50': 'hsl(var(--purple-50))',
          '100': 'hsl(var(--purple-100))',
          '200': 'hsl(var(--purple-200))',
          '300': 'hsl(var(--purple-300))',
          '400': 'hsl(var(--purple-400))',
          '500': 'hsl(var(--purple-500))',
          '600': 'hsl(var(--purple-600))',
          '700': 'hsl(var(--purple-700))',
          '800': 'hsl(var(--purple-800))',
          '900': 'hsl(var(--purple-900))',
          '950': 'hsl(var(--purple-950))',
          'alpha-24': 'hsl(var(--purple-alpha-24))',
          'alpha-16': 'hsl(var(--purple-alpha-16))',
          'alpha-10': 'hsl(var(--purple-alpha-10))',
        },
        sky: {
          '50': 'hsl(var(--sky-50))',
          '100': 'hsl(var(--sky-100))',
          '200': 'hsl(var(--sky-200))',
          '300': 'hsl(var(--sky-300))',
          '400': 'hsl(var(--sky-400))',
          '500': 'hsl(var(--sky-500))',
          '600': 'hsl(var(--sky-600))',
          '700': 'hsl(var(--sky-700))',
          '800': 'hsl(var(--sky-800))',
          '900': 'hsl(var(--sky-900))',
          '950': 'hsl(var(--sky-950))',
          'alpha-24': 'hsl(var(--sky-alpha-24))',
          'alpha-16': 'hsl(var(--sky-alpha-16))',
          'alpha-10': 'hsl(var(--sky-alpha-10))',
        },
        pink: {
          '50': 'hsl(var(--pink-50))',
          '100': 'hsl(var(--pink-100))',
          '200': 'hsl(var(--pink-200))',
          '300': 'hsl(var(--pink-300))',
          '400': 'hsl(var(--pink-400))',
          '500': 'hsl(var(--pink-500))',
          '600': 'hsl(var(--pink-600))',
          '700': 'hsl(var(--pink-700))',
          '800': 'hsl(var(--pink-800))',
          '900': 'hsl(var(--pink-900))',
          '950': 'hsl(var(--pink-950))',
          'alpha-24': 'hsl(var(--pink-alpha-24))',
          'alpha-16': 'hsl(var(--pink-alpha-16))',
          'alpha-10': 'hsl(var(--pink-alpha-10))',
        },
        teal: {
          '50': 'hsl(var(--teal-50))',
          '100': 'hsl(var(--teal-100))',
          '200': 'hsl(var(--teal-200))',
          '300': 'hsl(var(--teal-300))',
          '400': 'hsl(var(--teal-400))',
          '500': 'hsl(var(--teal-500))',
          '600': 'hsl(var(--teal-600))',
          '700': 'hsl(var(--teal-700))',
          '800': 'hsl(var(--teal-800))',
          '900': 'hsl(var(--teal-900))',
          '950': 'hsl(var(--teal-950))',
          'alpha-24': 'hsl(var(--teal-alpha-24))',
          'alpha-16': 'hsl(var(--teal-alpha-16))',
          'alpha-10': 'hsl(var(--teal-alpha-10))',
        },
        white: {
          DEFAULT: '#fff',
          'alpha-24': 'hsl(var(--white-alpha-24))',
          'alpha-16': 'hsl(var(--white-alpha-16))',
          'alpha-10': 'hsl(var(--white-alpha-10))',
        },
        black: {
          DEFAULT: '#000',
          'alpha-24': 'hsl(var(--black-alpha-24))',
          'alpha-16': 'hsl(var(--black-alpha-16))',
          'alpha-10': 'hsl(var(--black-alpha-10))',
        },
        primary: {
          dark: 'hsl(var(--primary-dark))',
          darker: 'hsl(var(--primary-darker))',
          base: 'hsl(var(--primary-base))',
          'alpha-24': 'hsl(var(--primary-alpha-24))',
          'alpha-16': 'hsl(var(--primary-alpha-16))',
          'alpha-10': 'hsl(var(--primary-alpha-10))',
        },
        static: {
          black: 'hsl(var(--static-black))',
          white: 'hsl(var(--static-white))',
        },
        bg: {
          'strong-950': 'hsl(var(--bg-strong-950))',
          'surface-800': 'hsl(var(--bg-surface-800))',
          'sub-300': 'hsl(var(--bg-sub-300))',
          'soft-200': 'hsl(var(--bg-soft-200))',
          'weak-50': 'hsl(var(--bg-weak-50))',
          'white-0': 'hsl(var(--bg-white-0))',
        },
        text: {
          'strong-950': 'hsl(var(--text-strong-950))',
          'sub-600': 'hsl(var(--text-sub-600))',
          'soft-400': 'hsl(var(--text-soft-400))',
          'disabled-300': 'hsl(var(--text-disabled-300))',
          'white-0': 'hsl(var(--text-white-0))',
        },
        stroke: {
          'strong-950': 'hsl(var(--stroke-strong-950))',
          'sub-300': 'hsl(var(--stroke-sub-300))',
          'soft-200': 'hsl(var(--stroke-soft-200))',
          'white-0': 'hsl(var(--stroke-white-0))',
        },
        faded: {
          dark: 'hsl(var(--faded-dark))',
          base: 'hsl(var(--faded-base))',
          light: 'hsl(var(--faded-light))',
          lighter: 'hsl(var(--faded-lighter))',
        },
        information: {
          dark: 'hsl(var(--information-dark))',
          base: 'hsl(var(--information-base))',
          light: 'hsl(var(--information-light))',
          lighter: 'hsl(var(--information-lighter))',
        },
        warning: {
          dark: 'hsl(var(--warning-dark))',
          base: 'hsl(var(--warning-base))',
          light: 'hsl(var(--warning-light))',
          lighter: 'hsl(var(--warning-lighter))',
        },
        error: {
          dark: 'hsl(var(--error-dark))',
          darker: 'hsl(var(--error-darker))',
          base: 'hsl(var(--error-base))',
          light: 'hsl(var(--error-light))',
          lighter: 'hsl(var(--error-lighter))',
        },
        success: {
          dark: 'hsl(var(--success-dark))',
          base: 'hsl(var(--success-base))',
          light: 'hsl(var(--success-light))',
          lighter: 'hsl(var(--success-lighter))',
        },
        away: {
          dark: 'hsl(var(--away-dark))',
          base: 'hsl(var(--away-base))',
          light: 'hsl(var(--away-light))',
          lighter: 'hsl(var(--away-lighter))',
        },
        feature: {
          dark: 'hsl(var(--feature-dark))',
          base: 'hsl(var(--feature-base))',
          light: 'hsl(var(--feature-light))',
          lighter: 'hsl(var(--feature-lighter))',
        },
        verified: {
          dark: 'hsl(var(--verified-dark))',
          base: 'hsl(var(--verified-base))',
          light: 'hsl(var(--verified-light))',
          lighter: 'hsl(var(--verified-lighter))',
        },
        highlighted: {
          dark: 'hsl(var(--highlighted-dark))',
          base: 'hsl(var(--highlighted-base))',
          light: 'hsl(var(--highlighted-light))',
          lighter: 'hsl(var(--highlighted-lighter))',
        },
        stable: {
          dark: 'hsl(var(--stable-dark))',
          base: 'hsl(var(--stable-base))',
          light: 'hsl(var(--stable-light))',
          lighter: 'hsl(var(--stable-lighter))',
        },
        social: {
          apple: 'hsl(var(--social-apple))',
          twitter: 'hsl(var(--social-twitter))',
          github: 'hsl(var(--social-github))',
          google: 'hsl(var(--social-google))',
          facebook: 'hsl(var(--social-facebook))',
          linkedin: 'hsl(var(--social-linkedin))',
          dropbox: 'hsl(var(--social-dropbox))',
          notion: 'hsl(var(--social-notion))',
          tidal: 'hsl(var(--social-tidal))',
          amazon: 'hsl(var(--social-amazon))',
          zendesk: 'hsl(var(--social-zendesk))',
        },
        overlay: {
          DEFAULT: 'hsl(var(--overlay))',
        },
        illustration: {
          'white-0': 'hsl(var(--illustration-white-0))',
          'weak-100': 'hsl(var(--illustration-weak-100))',
          'soft-200': 'hsl(var(--illustration-soft-200))',
          'sub-300': 'hsl(var(--illustration-sub-300))',
          'strong-400': 'hsl(var(--illustration-strong-400))',
        },
      },
      fontFamily: {
        // Переменные объявлены в src/app/globals.css, сам шрифт подключает index.html.
        sans: ['var(--font-inter)'],
        'inter-var': ['var(--font-inter-var)'],
      },
      fontSize: texts,
      boxShadow: shadows,
      spacing: {
        '4.5': '1.125rem',
        '5.5': '1.375rem',
        '18': '4.5rem',
        '70': '17.5rem',
        '75': '18.75rem',
        '95': '23.75rem',
        '100': '25rem',
        '104': '26rem',
      },
      minHeight: {
        '50': '12.5rem',
        '20.5': '5.125rem',
      },
      blur: {
        '10': '10px',
      },
      borderRadius: {
        ...borderRadii,
        '3': '3px',
      },
      animation: {
        // Однократная подсветка изменившегося числа. Правило 11 `ПРАВИЛА.md`: «300 мс — подмена
        // свежими с однократной подсветкой изменившихся». До 03.09.2026 подсветки не было вовсе:
        // число молча подменялось, и человек, смотревший на экран, не узнавал, что оно поехало.
        // Цвет и длительность те же, что на телефоне (`AppTheme.Colors.freshHighlight`): синий на
        // шестнадцати процентах. Синий, а не зелёный и не красный — в этом продукте оба цвета
        // заняты смыслом, а «изменилось» смысла не несёт.
        fresh: 'fresh 1200ms ease-out 1',
        'accordion-down': 'accordion-down 0.2s ease-out',
        'accordion-up': 'accordion-up 0.2s ease-out',
        'event-item-show': 'event-item-show .5s cubic-bezier(0.6, 1, 0.75, 0.9) forwards',
      },
      keyframes: {
        fresh: {
          '0%': { backgroundColor: 'hsl(var(--information-base) / 0.16)' },
          '100%': { backgroundColor: 'transparent' },
        },
        'accordion-down': {
          from: { height: '0', opacity: '0' },
          to: { height: 'var(--radix-accordion-content-height)', opacity: '1' },
        },
        'accordion-up': {
          from: {
            height: 'var(--radix-accordion-content-height)',
            opacity: '1',
          },
          to: { height: '0', opacity: '0' },
        },
        'event-item-show': {
          to: { opacity: '1', transform: 'translate3d(0,0,0)' },
        },
      },
    },
  },

  future: {
    // Наведение мышью на планшете «залипает»: после нажатия элемент остаётся подсвеченным,
    // пока не тронут другой. Эта строка переписывает ВСЕ 88 наведений продукта разом, включая
    // те, что лежат в примитивах дизайн-системы и обновляются от поставщика.
    hoverOnlyWhenSupported: true,
  },

  plugins: [animate, вариантыСреды],
} satisfies Config;
